import type { FlowSankeyLinkSpec, FlowSankeyNodeSpec } from '../charts/flowSankeyLayout';
import type { ScenarioCompareByCohort, ScenarioUsePhaseByCohort } from '@siliconx/shared/dto/financials';

/**
 * SIL-517 — pure spec builders for the Compare step's two time-axis Sankeys
 * (asset value, carbon), same split-out-of-React convention as SIL-516's
 * `buildLifecycleSankeySpec`. Ported from the validated prototype's figB2a/
 * figB2b bookkeeping (design-eval artifact linked from SIL-517) — every
 * arithmetic rule below has a 1:1 comment pointing at the prototype line it
 * mirrors so a reviewer can diff against the `<script>` directly.
 *
 * Both builders are driven entirely by `POST /compare`'s `byCohort[].series`
 * / `byCohort[].carbonSeries` (SIL-517 engine plumbing, scenario-compare.ts)
 * — NEVER an equal-cohort-value split (the prototype's own documented
 * limitation, "valid for the demo scenario only").
 *
 * Columns are placed time-proportionally (`colPos`, `FlowSankey`'s existing
 * generic support) at month 0, the horizon end, and every cohort's own
 * action month — so path divergence always lands on a column, per spec.
 */

const EPS = 0.005;

const FILL = {
  cohort: 'fill-violet-500',
  decay: 'fill-zinc-400',
  writeOff: 'fill-red-500',
  recovered: 'fill-emerald-500',
  residual: 'fill-emerald-500',
  newGear: 'fill-red-500',
  usePhase: 'fill-amber-500',
  embodied: 'fill-emerald-500',
} as const;

// ---------------------------------------------------------------------------
// Value diagram (prototype figB2a)
// ---------------------------------------------------------------------------

export interface ScenarioTimeSankeySpec {
  nodes: FlowSankeyNodeSpec[];
  links: FlowSankeyLinkSpec[];
  colPos: Record<number, number>;
  colLabels: Record<number, string>;
}

/**
 * A cohort's action month, derived from its OWN monthly series rather than a
 * second lookup — the first month a `sell`/`itad` recovery or a `refresh`
 * capex appears (both start at exactly 0 and turn non-zero once, staying
 * fixed after — `scenarioCohortAt`'s docstring). `null` for `hold` (or any
 * cohort whose scenario arm never diverges from baseline) — the "hold-only:
 * still valid, lanes just run flat" edge case in spec.
 */
function detectValueActionMonth(series: ScenarioCompareByCohort['series']): number | null {
  if (!series || series.length === 0) return null;
  for (let i = 1; i < series.length; i += 1) {
    const cur = series[i]!.scenario;
    const prev = series[i - 1]!.scenario;
    if (cur.recoveredValueUsd > prev.recoveredValueUsd + EPS || cur.refreshCapexUsd > prev.refreshCapexUsd + EPS) {
      return series[i]!.month;
    }
  }
  return null;
}

function fmtUsdTip(usd: (v: number) => string, label: string, value: number): string {
  return `${label}\n${usd(value)}`;
}

/**
 * Builds the asset-value time-Sankey spec (prototype figB2a). One lane per
 * cohort: starts at the cohort's month-0 residual FMV, per-interval decay
 * peels off into a SHARED cumulative decay lane (pooled across cohorts —
 * prototype's single `d0..d36` chain), and at the action month the lane
 * honestly resolves: `write-off = baseline residual at that month minus
 * whatever was actually recovered` (0 for `refresh`, the ITAD baseline for
 * `itad`, the market FMV for `sell` — the last case nets to a ~0 write-off
 * since `sell`'s `recoveredValueUsd` IS that same baseline FMV). Recovered
 * value > 0 exits as its own emerald node, never folded into "written off"
 * (spec edge case). `refresh` additionally opens a NEW lane at the action
 * month, fed by an annotated "new gear" source (col 0) sized to the
 * NEW fleet's tracked FMV, never the capex (`refreshCapexUsd` is text-only
 * on that source node's `cap` — "capex annotated, never drawn", prototype
 * `figB2a`'s note: at true scale it crushes the diagram ~150:1).
 */
export function buildScenarioValueTimeSankeySpec(
  byCohort: ScenarioCompareByCohort[],
  horizonMonths: number,
  formatUsd: (v: number) => string,
): ScenarioTimeSankeySpec {
  const withSeries = byCohort.filter((c) => c.series && c.series.length > 0);

  const actionMonths = withSeries
    .map((c) => detectValueActionMonth(c.series))
    .filter((m): m is number => m != null && m > 0 && m < horizonMonths);

  const cols = [...new Set([0, horizonMonths, ...actionMonths])].sort((a, b) => a - b);
  const colPos: Record<number, number> = {};
  const colLabels: Record<number, string> = {};
  cols.forEach((c) => {
    colPos[c] = horizonMonths > 0 ? c / horizonMonths : 0;
    const isAction = actionMonths.includes(c);
    colLabels[c] = c === 0 ? 'MONTH 0' : isAction ? `M${c} · ACTION` : `M${c}`;
  });

  const nodes: FlowSankeyNodeSpec[] = [];
  const links: FlowSankeyLinkSpec[] = [];
  const seen = new Set<string>();
  const addNode = (n: FlowSankeyNodeSpec) => {
    if (seen.has(n.id)) return;
    seen.add(n.id);
    nodes.push(n);
  };

  // Decay inflow events collected per cohort, wired into the shared
  // accumulator lane in a second pass (needs every cohort's contribution at
  // a column before the accumulator's own running total is known).
  const decayEvents: Array<{ col: number; sourceId: string; value: number; tip: string }> = [];

  withSeries.forEach((cohort) => {
    const series = cohort.series!;
    const byMonth = new Map(series.map((p) => [p.month, p]));
    const actionMonth = detectValueActionMonth(series);
    const point0 = byMonth.get(0);
    if (!point0) return;

    const v0Id = `${cohort.cohortId}::v0`;
    addNode({
      id: v0Id,
      col: 0,
      value: point0.scenario.residualFmvUsd,
      label: cohort.label,
      cap: `month-0 FMV${actionMonth == null ? ' · held' : ` · acts month ${actionMonth}`}`,
      fillClass: FILL.cohort,
    });

    let prevNodeId = v0Id;
    let prevCol = 0;
    let ended = false;

    for (const col of cols) {
      if (col === 0 || ended) continue;
      const point = byMonth.get(col);
      if (!point) continue;

      const preAction = actionMonth == null || col <= actionMonth;
      const arm = preAction ? point.baseline : point.scenario;
      const prevPoint = byMonth.get(prevCol);
      const prevArm = preAction ? prevPoint?.baseline : prevPoint?.scenario;
      const decayAmt = Math.max(0, (prevArm?.residualFmvUsd ?? 0) - arm.residualFmvUsd);
      if (decayAmt > EPS) {
        decayEvents.push({
          col,
          sourceId: prevNodeId,
          value: decayAmt,
          tip: fmtUsdTip(formatUsd, `${cohort.label} decay, months ${prevCol}–${col}`, decayAmt),
        });
      }

      if (actionMonth != null && col === actionMonth) {
        // Old lane resolves honestly: recovered (if any) exits emerald,
        // the unrecovered remainder is a write-off — never merged (edge
        // case: recoveredValueUsd > 0 must exit as its own outflow).
        const baselineResidual = point.baseline.residualFmvUsd;
        const recovered = point.scenario.recoveredValueUsd;
        const writeOff = Math.max(0, baselineResidual - recovered);
        if (recovered > EPS) {
          const recId = `${cohort.cohortId}::recovered`;
          addNode({
            id: recId,
            col,
            label: 'Recovered value',
            cap: 'sell/ITAD proceeds',
            fillClass: FILL.recovered,
          });
          links.push({
            source: prevNodeId,
            target: recId,
            value: recovered,
            fillClass: FILL.recovered,
            tip: fmtUsdTip(formatUsd, `${cohort.label} — recovered at month ${col}`, recovered),
          });
        }
        if (writeOff > EPS) {
          const woId = `${cohort.cohortId}::writeoff`;
          addNode({
            id: woId,
            col,
            label: recovered > EPS ? undefined : 'Written off',
            cap: recovered > EPS ? undefined : `recovered ${formatUsd(recovered)}`,
            fillClass: FILL.writeOff,
          });
          links.push({
            source: prevNodeId,
            target: woId,
            value: writeOff,
            fillClass: FILL.writeOff,
            tip: fmtUsdTip(formatUsd, `${cohort.label} — written off at month ${col}`, writeOff),
          });
        }

        // Refresh opens a NEW lane here, fed by an annotated "new gear"
        // source at col 0 (never the actual capex figure — see docstring).
        const newFmv = point.scenario.residualFmvUsd;
        const capex = point.scenario.refreshCapexUsd;
        if (capex > EPS) {
          const srcId = `${cohort.cohortId}::newgear`;
          addNode({
            id: srcId,
            col: 0,
            label: `${cohort.label} — new gear`,
            cap: `capex ${formatUsd(capex)} — annotated, not drawn`,
            subText: `${formatUsd(newFmv)} FMV in`,
            fillClass: FILL.newGear,
          });
          const newNodeId = `${cohort.cohortId}::v${col}`;
          addNode({ id: newNodeId, col, fillClass: FILL.newGear });
          links.push({
            source: srcId,
            target: newNodeId,
            value: newFmv,
            fillClass: FILL.newGear,
            tip: `New gear enters at month ${col}\n${formatUsd(newFmv)} tracked FMV (capex ${formatUsd(capex)})`,
          });
          prevNodeId = newNodeId;
          prevCol = col;
          continue;
        }
        // sell/itad: no replacement lane — this cohort's story ends here.
        ended = true;
        continue;
      }

      // Ordinary continuation (hold, or refresh's post-action new lane).
      const remaining = arm.residualFmvUsd;
      const isLast = col === cols[cols.length - 1];
      const nodeId = `${cohort.cohortId}::v${col}`;
      addNode({
        id: nodeId,
        col,
        label: isLast ? `${cohort.label} — residual` : undefined,
        cap: isLast ? 'still tracked' : undefined,
        fillClass: isLast ? FILL.residual : FILL.cohort,
      });
      if (remaining > EPS || decayAmt > EPS) {
        links.push({
          source: prevNodeId,
          target: nodeId,
          value: remaining,
          fillClass: isLast ? FILL.residual : FILL.cohort,
        });
      }
      prevNodeId = nodeId;
      prevCol = col;
    }
  });

  // Second pass: shared decay accumulator lane, one node per column that
  // received a decay inflow, cumulative total carried forward each step.
  let running = 0;
  let prevDecayCol: number | null = null;
  for (const col of cols) {
    const inflows = decayEvents.filter((e) => e.col === col);
    if (inflows.length === 0 && running === 0) continue;
    const decayId = `decay::${col}`;
    if (running > EPS && prevDecayCol != null) {
      links.push({
        source: `decay::${prevDecayCol}`,
        target: decayId,
        value: running,
        fillClass: FILL.decay,
      });
    }
    inflows.forEach((e) => {
      links.push({ source: e.sourceId, target: decayId, value: e.value, fillClass: FILL.decay, tip: e.tip });
    });
    running += inflows.reduce((a, e) => a + e.value, 0);
    const isLast = col === cols[cols.length - 1];
    addNode({
      id: decayId,
      col,
      label: isLast ? 'Cumulative FMV decay' : undefined,
      cap: isLast ? 'value lost to depreciation, all lanes' : undefined,
      fillClass: FILL.decay,
    });
    prevDecayCol = col;
  }

  return { nodes, links, colPos, colLabels };
}

// ---------------------------------------------------------------------------
// Carbon diagram (prototype figB2b)
// ---------------------------------------------------------------------------

/**
 * Builds the carbon time-Sankey spec (prototype figB2b). One lane per
 * cohort, NOT pooled (unlike the value diagram's shared decay lane) — each
 * cohort's use-phase accrual is its own chain: a "carry" link (the running
 * total so far) plus a per-interval "new accrual" inflow from an unlabeled
 * col-0 phantom source (mirrors the prototype's `sa2`/`sa3`/`sa4` nodes —
 * new inflow is additive, never carved out of the running total the way the
 * value diagram's decay genuinely IS a split of residual FMV). A cohort
 * whose `useCarbon.byCohort` entry has `carbonPostActionUnmodeled` (or whose
 * accrual simply stopped because it left the fleet at `carbonAccrualStopMonth`)
 * freezes there — the lane terminates at that column rather than continuing
 * flat to the horizon, an honest "stops here" rather than a padded frozen
 * ribbon. A `refresh` cohort's replacement-gear embodied carbon (cat 1/2)
 * lands as a ONE-TIME inflow at that same column, from a distinct source.
 */
export function buildScenarioCarbonTimeSankeySpec(
  byCohort: ScenarioCompareByCohort[],
  useCarbonByCohort: ScenarioUsePhaseByCohort[],
  horizonMonths: number,
  formatCarbon: (kg: number) => string,
): ScenarioTimeSankeySpec {
  const useCarbonById = new Map(useCarbonByCohort.map((c) => [c.cohortId, c]));
  const withSeries = byCohort.filter((c) => c.carbonSeries && c.carbonSeries.length > 0);

  const stopMonths = withSeries
    .map((c) => useCarbonById.get(c.cohortId)?.carbonAccrualStopMonth)
    .filter((m): m is number => m != null && m > 0 && m < horizonMonths);

  const cols = [...new Set([0, horizonMonths, ...stopMonths])].sort((a, b) => a - b);
  const colPos: Record<number, number> = {};
  const colLabels: Record<number, string> = {};
  cols.forEach((c) => {
    colPos[c] = horizonMonths > 0 ? c / horizonMonths : 0;
    const isAction = stopMonths.includes(c);
    colLabels[c] = c === 0 ? 'MONTH 0' : isAction ? `M${c} · ACTION` : `M${c}`;
  });

  const nodes: FlowSankeyNodeSpec[] = [];
  const links: FlowSankeyLinkSpec[] = [];
  const seen = new Set<string>();
  const addNode = (n: FlowSankeyNodeSpec) => {
    if (seen.has(n.id)) return;
    seen.add(n.id);
    nodes.push(n);
  };
  const usePhaseAt = (p: { scope2: number; cat8: number }) => p.scope2 + p.cat8;

  withSeries.forEach((cohort) => {
    const series = cohort.carbonSeries!;
    const byMonth = new Map(series.map((p) => [p.month, p]));
    const useCarbon = useCarbonById.get(cohort.cohortId);
    const stopMonth = useCarbon?.carbonAccrualStopMonth ?? horizonMonths;
    const frozen = stopMonth < horizonMonths;
    const point0 = byMonth.get(0);
    if (!point0) return;

    // Replacement-gear embodied carbon (cat 1/2) — a one-time inflow that
    // lands exactly at the freeze column, computed up front so the leaf
    // node's color/label can already reflect it (never merged into the
    // use-phase total itself).
    const stopPoint = byMonth.get(stopMonth);
    const embodied = useCarbon?.carbonPostActionUnmodeled ? stopPoint?.scenario.cat1_or_2 ?? 0 : 0;
    const hasEmbodied = embodied > EPS;

    // Month 0's cumulative accrual is ALWAYS exactly zero — nothing has
    // been emitted yet at the start of the clock (buildCarbonScenarioSeries
    // sums rate x elapsed months, and zero months have elapsed). That is a
    // true, structural zero, not a suppressed or missing figure — but the
    // generic FlowSankey layout can't tell "definitionally zero" apart from
    // "computed and merely tiny," so it flags this node `negligible` and
    // FlowSankey renders "0 kgCO2e ≈0" right beside sibling lanes reporting
    // real tonnage. That reads as a data bug (SIL — sankey zero-lane
    // suppression). Fix, kept component-internal since FlowSankey itself is
    // out of scope: give this anchor no label — same "unlabeled mid-column
    // node: tooltip-only" treatment FlowSankey already applies elsewhere —
    // so no bare zero prints in the diagram; the honest number for this
    // cohort shows up at the node that actually carries one (this lane's
    // frozen/emitted leaf, below). The single exception is a cohort whose
    // action lands AT month 0 (`stopMonth === 0`): there this IS the
    // cohort's only node in the diagram, so it keeps its label — with an
    // explicit reason instead of a bare "0 kgCO2e ≈0" — rather than the
    // cohort silently vanishing from the chart.
    const activeCols = cols.filter((c) => c <= stopMonth || c === 0);
    const onlyMonthZero = activeCols.length <= 1;

    const c0Id = `${cohort.cohortId}::c0`;
    addNode({
      id: c0Id,
      col: 0,
      value: usePhaseAt(point0.scenario),
      label: onlyMonthZero ? cohort.label : undefined,
      cap: onlyMonthZero
        ? 'acted at month 0 — no use-phase accrual modeled before that'
        : frozen
          ? `use-phase accrual, stops month ${stopMonth}`
          : `use-phase accrual, ${horizonMonths}mo`,
      subText: onlyMonthZero ? 'no accrual before action' : undefined,
      tip: onlyMonthZero
        ? undefined
        : `${cohort.label} — use-phase accrual starts here, month 0; nothing has been emitted yet`,
      fillClass: FILL.usePhase,
    });

    let prevNodeId = c0Id;
    let prevTotal = usePhaseAt(point0.scenario);
    let prevCol = 0;

    for (const col of activeCols) {
      if (col === 0) continue;
      const point = byMonth.get(col);
      if (!point) continue;
      const total = usePhaseAt(point.scenario);
      const delta = Math.max(0, total - prevTotal);

      const nodeId = `${cohort.cohortId}::c${col}`;
      const isLast = col === activeCols[activeCols.length - 1];
      addNode({
        id: nodeId,
        col,
        label: isLast ? `${cohort.label} — ${frozen ? 'frozen' : 'emitted'}` : undefined,
        cap: isLast ? (frozen ? 'post-action use-phase unmodeled' : `use-phase, ${horizonMonths}mo`) : undefined,
        // Emerald only when this leaf actually carries embodied carbon too
        // (a plain frozen use-phase-only leaf, e.g. sell/itad, stays amber —
        // matches the prototype's A36 amber vs B36 emerald distinction).
        fillClass: isLast && hasEmbodied ? FILL.embodied : FILL.usePhase,
      });

      if (prevTotal > EPS) {
        links.push({ source: prevNodeId, target: nodeId, value: prevTotal, fillClass: FILL.usePhase });
      }
      if (delta > EPS) {
        const srcId = `${cohort.cohortId}::inflow${col}`;
        addNode({ id: srcId, col: 0, value: delta, fillClass: FILL.usePhase });
        links.push({
          source: srcId,
          target: nodeId,
          value: delta,
          fillClass: FILL.usePhase,
          tip: `${cohort.label} use-phase, months ${prevCol}–${col}\n${formatCarbon(delta)}`,
        });
      }

      prevNodeId = nodeId;
      prevTotal = total;
      prevCol = col;
    }

    if (hasEmbodied) {
      const embId = `${cohort.cohortId}::embodied`;
      addNode({
        id: embId,
        col: 0,
        label: `${cohort.label} — new-gear embodied`,
        cap: 'cat 1/2, lands at action month',
        fillClass: FILL.embodied,
      });
      links.push({
        source: embId,
        target: `${cohort.cohortId}::c${stopMonth}`,
        value: embodied,
        fillClass: FILL.embodied,
        tip: `Replacement hardware embodied carbon\n${formatCarbon(embodied)}, at month ${stopMonth}`,
      });
    }
  });

  return { nodes, links, colPos, colLabels };
}
